import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import { image } from "../../shared/assets/manifest";

const MODE_KEY = "classroomMode";
const SILENT_KEY = "classroomQuickSilent";

type Listener = () => void;

const modeListeners = new Set<Listener>();
const silentListeners = new Set<Listener>();

function readMode() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("classroom") === "1") return true;
  return localStorage.getItem(MODE_KEY) === "1";
}

function readSilent() {
  if (typeof window === "undefined") return true;
  return localStorage.getItem(SILENT_KEY) !== "0";
}

function emit(listeners: Set<Listener>) {
  listeners.forEach((listener) => listener());
}

export function setClassroomMode(enabled: boolean) {
  localStorage.setItem(MODE_KEY, enabled ? "1" : "0");
  document.body.dataset.classroomMode = enabled ? "1" : "0";
  emit(modeListeners);
}

export function setClassroomQuickSilent(enabled: boolean) {
  localStorage.setItem(SILENT_KEY, enabled ? "1" : "0");
  emit(silentListeners);
}

export function useClassroomMode() {
  const enabled = useSyncExternalStore(
    (onStoreChange) => {
      modeListeners.add(onStoreChange);
      return () => modeListeners.delete(onStoreChange);
    },
    readMode,
    () => false
  );

  useEffect(() => {
    document.body.dataset.classroomMode = enabled ? "1" : "0";
  }, [enabled]);

  const setEnabled = useCallback((next: boolean) => setClassroomMode(next), []);
  const toggle = useCallback(() => setClassroomMode(!readMode()), []);

  return { enabled, setEnabled, toggle };
}

export function useClassroomQuickSilent() {
  const enabled = useSyncExternalStore(
    (onStoreChange) => {
      silentListeners.add(onStoreChange);
      return () => silentListeners.delete(onStoreChange);
    },
    readSilent,
    () => true
  );
  const setEnabled = useCallback((next: boolean) => setClassroomQuickSilent(next), []);
  return { enabled, setEnabled };
}

/** One-shot bootstrap: honor ?classroom=1 and optional ?class= switch. */
export function useClassroomBootstrap(onSwitchClass?: (classId: number) => void) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (done) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("classroom") === "1") {
      setClassroomMode(true);
      const classId = Number(params.get("class"));
      if (Number.isFinite(classId) && classId > 0) onSwitchClass?.(classId);
      params.delete("classroom");
      params.delete("class");
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash}`;
      // Preserve the router's history index so Back can still block unsaved exits.
      window.history.replaceState(window.history.state, "", next);
    } else {
      document.body.dataset.classroomMode = readMode() ? "1" : "0";
    }
    setDone(true);
  }, [done, onSwitchClass]);
}

export const OVERVIEW_QUICK_ACTIONS = [
  {
    icon: image.quickPlus1,
    label: "+1",
    points: 1,
    tone: "good",
    reason: "太棒了"
  },
  {
    icon: image.quickPlus3,
    label: "+3",
    points: 3,
    tone: "good",
    reason: "非常棒"
  },
  {
    icon: image.quickBell,
    label: "提醒",
    points: -1,
    tone: "neutral",
    reason: "课堂提醒"
  }
] as const;

export const OVERVIEW_DOCK_REWARDS = [
  { label: "太棒了", points: 1, icon: image.dockReward1, reason: "太棒了" },
  { label: "很不错", points: 2, icon: image.dockReward2, reason: "很不错" },
  { label: "非常棒", points: 3, icon: image.dockReward3, reason: "非常棒" },
  { label: "做得好", points: 4, icon: image.dockReward4, reason: "做得好" },
  { label: "超级赞", points: 5, icon: image.dockReward5, reason: "超级赞" }
] as const;
